import type { CaravanDb } from './db'
import {
  OUTBOX_BATCH_SIZE,
  computeBackoffMs,
  sanitizedSyncMessage,
  type MutationReceipt,
  type OutboxMutation,
} from '../../domain/sync'

export class OutboxRepository {
  readonly #db: CaravanDb

  constructor(db: CaravanDb) {
    this.#db = db
  }

  async recoverInterruptedWork(): Promise<number> {
    return this.#db.outbox.where('state').equals('syncing').modify({ state: 'pending' })
  }

  async takeDueBatch(now: number): Promise<OutboxMutation[]> {
    return this.#db.transaction('rw', this.#db.outbox, async () => {
      const candidates = await this.#db.outbox
        .where('state')
        .equals('pending')
        .filter((mutation) => mutation.nextAttemptAt <= now)
        .sortBy('createdAtDevice')
      const batch = candidates.slice(0, OUTBOX_BATCH_SIZE)
      for (const mutation of batch) {
        mutation.state = 'syncing'
        mutation.attemptCount += 1
        await this.#db.outbox.put(mutation)
      }
      return batch
    })
  }

  async reschedule(
    mutations: OutboxMutation[],
    now: number,
    random: () => number,
    message: unknown,
    retryAfterMs: number | null = null,
  ): Promise<void> {
    await this.#db.transaction('rw', this.#db.outbox, async () => {
      for (const mutation of mutations) {
        await this.#db.outbox.update(mutation.mutationId, {
          state: 'pending',
          nextAttemptAt: now + (retryAfterMs ?? computeBackoffMs(mutation.attemptCount, random)),
          lastErrorCode: 'TRANSIENT',
          lastErrorMessage: sanitizedSyncMessage(message),
        })
      }
    })
  }

  async applyReceipts(receipts: MutationReceipt[]): Promise<void> {
    await this.#db.transaction(
      'rw',
      this.#db.outbox,
      this.#db.entities,
      this.#db.histories,
      async () => {
        for (const receipt of receipts) {
          const mutation = await this.#db.outbox.get(receipt.mutationId)
          if (!mutation) continue
          const entity = await this.#db.entities.get(`${mutation.entityType}:${mutation.entityId}`)
          if (receipt.outcome === 'applied' || receipt.outcome === 'duplicate') {
            await this.#db.outbox.update(mutation.mutationId, {
              state: 'synced',
              nextAttemptAt: 0,
              lastErrorCode: null,
              lastErrorMessage: null,
            })
            if (entity) {
              await this.#db.entities.update(entity.key, {
                syncState: 'synced',
                version: receipt.resultingVersion ?? entity.version,
                updatedAtServer: receipt.receivedAtServer,
                lastError: null,
              })
            }
          } else if (receipt.outcome === 'conflict') {
            await this.#db.outbox.update(mutation.mutationId, {
              state: 'conflict',
              lastErrorCode: receipt.errorCode ?? 'VERSION_CONFLICT',
              lastErrorMessage: sanitizedSyncMessage(receipt.errorMessage),
            })
            if (entity && receipt.serverEntity) {
              await this.#db.entities.update(entity.key, {
                syncState: 'conflict',
                lastError: 'Une version distante doit être comparée.',
              })
              await this.#db.histories.put({
                id: `${mutation.entityType}:${mutation.entityId}:${mutation.mutationId}`,
                entityType: mutation.entityType,
                entityId: mutation.entityId,
                localPayload: structuredClone(entity.payload),
                serverPayload: structuredClone(receipt.serverEntity.payload),
                localVersion: entity.version,
                serverVersion: receipt.serverEntity.version,
                recordedAt: receipt.receivedAtServer,
                replacingMutationId: mutation.mutationId,
              })
            }
          } else {
            await this.#db.outbox.update(mutation.mutationId, {
              state: 'failed',
              lastErrorCode: receipt.errorCode ?? 'REJECTED',
              lastErrorMessage: sanitizedSyncMessage(receipt.errorMessage),
            })
            if (entity) {
              await this.#db.entities.update(entity.key, {
                syncState: 'failed',
                lastError: sanitizedSyncMessage(receipt.errorMessage),
              })
            }
          }
        }
      },
    )
  }

  async retryActionable(): Promise<number> {
    return this.#db.outbox
      .where('state')
      .anyOf(['pending', 'failed', 'conflict'])
      .modify({ state: 'pending', nextAttemptAt: 0, lastErrorCode: null, lastErrorMessage: null })
  }
}
